import React, { useState, useEffect, useCallback, useRef } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * NotificationToast — Slides in from top, displays for 7 seconds, then fades out.
 * Polls for new unread notifications and shows them one at a time.
 */
export default function NotificationToast({ userId }) {
  const [toast, setToast] = useState(null);
  const [visible, setVisible] = useState(false);
  const [fading, setFading] = useState(false);
  const shownIdsRef = useRef(new Set());
  const timerRef = useRef(null);

  const checkForNew = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API}/api/inapp-notifications/${userId}?limit=1&unread_only=true`);
      const data = await res.json();
      const notifs = data.notifications || [];
      if (notifs.length > 0) {
        const n = notifs[0];
        if (!shownIdsRef.current.has(n.notification_id)) {
          shownIdsRef.current.add(n.notification_id);
          showToast(n);
        }
      }
    } catch {}
  }, [userId]);

  useEffect(() => {
    const interval = setInterval(checkForNew, 15000);
    checkForNew();
    return () => clearInterval(interval);
  }, [checkForNew]);

  const showToast = (notification) => {
    setToast(notification);
    setFading(false);
    setVisible(true);

    if (timerRef.current) clearTimeout(timerRef.current);

    // Start fade at 6s, fully gone at 7s
    timerRef.current = setTimeout(() => {
      setFading(true);
      setTimeout(() => {
        setVisible(false);
        setToast(null);
        setFading(false);
      }, 1000); // 1s fade animation
    }, 6000);
  };

  const dismiss = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setFading(true);
    setTimeout(() => {
      setVisible(false);
      setToast(null);
      setFading(false);
    }, 300);
  };

  if (!visible || !toast) return null;

  const typeColor = {
    price_drop: '#10b981',
    booking_confirmation: '#6366f1',
    system: '#ef4444',
    promo: '#f59e0b',
    info: '#3b82f6',
  }[toast.type] || '#D4AF37';

  return (
    <div
      data-testid="notification-toast"
      style={{
        position: 'fixed',
        top: '80px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 600,
        width: '90%',
        maxWidth: '420px',
        background: 'var(--bg-card, #1e293b)',
        border: `1px solid ${typeColor}40`,
        borderLeft: `4px solid ${typeColor}`,
        borderRadius: 'var(--radius-md, 10px)',
        boxShadow: 'var(--shadow-lg, 0 8px 24px rgba(0,0,0,0.4))',
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        animation: fading ? 'toastFadeOut 1s ease forwards' : 'toastSlideIn 0.4s cubic-bezier(0.16,1,0.3,1) forwards',
      }}
    >
      <span style={{
        width: '10px', height: '10px', borderRadius: '50%',
        background: typeColor, flexShrink: 0, marginTop: '4px',
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: '0.85rem', color: typeColor, marginBottom: '2px' }}>
          {toast.title}
        </div>
        <div style={{
          fontSize: '0.78rem', color: 'var(--text-secondary, #e2e8f0)',
          lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {toast.message}
        </div>
      </div>
      <button
        onClick={dismiss}
        style={{
          background: 'none', border: 'none', color: 'var(--text-dim, #64748b)',
          cursor: 'pointer', fontSize: '1rem', padding: '0 2px', flexShrink: 0,
        }}
        aria-label="Dismiss"
      >&times;</button>

      <style>{`
        @keyframes toastSlideIn {
          from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        @keyframes toastFadeOut {
          from { opacity: 1; transform: translateX(-50%) translateY(0); }
          to { opacity: 0; transform: translateX(-50%) translateY(-10px); }
        }
      `}</style>
    </div>
  );
}
